import React, { FC, useMemo, useState } from 'react';

import { EvMultiLineChart } from '@evinced-private/ui-common';

import { createStrategicTickPositions } from 'src/common/helpers/chartHelpers';

import { ERROR_TYPE } from '../../types/chartErrors';
import { ErrorView } from '../error-view/ErrorView';
import { ChartSkeleton } from '../skeletons/chart-skeleton/ChartSkeleton';

import {
	prepareDataForCriticalIssuesChart,
	prepareDataForUniqueIssuesChart
} from './helpers/dataTransformHelpers';
import { TABLE_PROPS } from './helpers/tableHelpers';
import { tooltipFormatter } from './helpers/tooltipHelpers';
import { CHART_CONSTANTS, CHART_TEXT } from './constants';
import { AccessibilityCriticalIssuesChartProps } from './types';

import './AccessibilityCriticalIssuesChart.scss';

export const AccessibilityCriticalIssuesChart: FC<AccessibilityCriticalIssuesChartProps> = ({
	data,
	uniqueIssuesData,
	error = null,
	isLoading = false,
	title = CHART_TEXT.TITLE,
	description = CHART_TEXT.DESCRIPTION,
	height = CHART_CONSTANTS.HEIGHT,
	showTable = true
}) => {
	const [showUniqueIssues, setShowUniqueIssues] = useState(false);

	const chartData = useMemo(() => {
		if (showUniqueIssues) {
			if (!uniqueIssuesData || uniqueIssuesData.length === 0) {
				return { categories: [], series: [] };
			}
			return prepareDataForUniqueIssuesChart(uniqueIssuesData);
		}

		if (!data || data.length === 0) {
			return { categories: [], series: [] };
		}
		return prepareDataForCriticalIssuesChart(data);
	}, [data, uniqueIssuesData, showUniqueIssues]);

	const hasError = useMemo(() => {
		if (showUniqueIssues) {
			return Boolean(error || !uniqueIssuesData || uniqueIssuesData.length === 0);
		}
		return Boolean(error || !data || data.length === 0);
	}, [error, data, uniqueIssuesData, showUniqueIssues]);

	const errorType = useMemo(() => (error ? ERROR_TYPE.FAILED : ERROR_TYPE.NO_DATA), [error]);

	if (isLoading) {
		return <ChartSkeleton />;
	}

	return (
		<div className="accessibility-critical-issues-chart">
			<div className="accessibility-critical-issues-chart-wrapper">
				<EvMultiLineChart
					title={title}
					series={chartData.series}
					description={description}
					chartHeadingLevel={3}
					yAxisLabel="Issues"
					height={height}
					className="accessibility-critical-issues-chart-inner"
					xAxis={{
						type: 'category' as const,
						categories: chartData.categories,
						tickPositions: createStrategicTickPositions(chartData.categories.length)
					}}
					showLegend={false}
					tooltipOptions={{
						formatter: tooltipFormatter
					}}
					enableMarker={data?.length === 1}
					isError={hasError}
					renderErrorState={() => <ErrorView errorType={errorType} />}
					yAxisAllowDecimals={false}
					{...(showTable && { tableProps: TABLE_PROPS })}
					{...(uniqueIssuesData &&
						uniqueIssuesData.length > 0 && {
							dataToggle: {
								options: [CHART_TEXT.ISSUES_TOGGLE.UNIQUE, CHART_TEXT.ISSUES_TOGGLE.ALL],
								checked: showUniqueIssues,
								onChange: setShowUniqueIssues,
								ariaLabel: CHART_TEXT.ISSUES_TOGGLE.ARIA_LABEL
							}
						})}
				/>
			</div>
		</div>
	);
};
